import { List } from 'immutable'
import { ReactElement, useState } from 'react'
import { Link } from 'react-router'

import { addAlertSignature } from '../../actions/alerts'
import { NewPayRoll } from '../../api/pay-rolls'
import { postStagedFile } from '../../api/staged-file'
import { applyStagedImport, postStagedImports } from '../../api/staged-import'
import paths from '../../constants/paths'
import Company from '../../model/company'
import Employee from '../../model/employee'
import SalaryCycle from '../../model/salaryCycle'
import SalaryPeriod from '../../model/salaryPeriod'
import { formatDate } from '../../utils/date-utils'
import { t } from '../../utils/translation-utils'
import Button from '../elements/button'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Headline from '../elements/Headline'
import Modal from '../elements/modal'
import Subcard from '../elements/Subcard'
import Subtitle from '../elements/Subtitle'
import Title from '../elements/Title'
import UserImage from '../elements/UserImage'
import jsBrowserHistory from '../widgets/jsBrowserHistory'
import PayRollAddSingleEmployee, { AdjustmentList, getTypeToText } from './PayRollAddSingleEmployee'
import PayRollsAddForm, { PayRollsAddResult } from './PayRollsAddForm'

import './PayRollAdd.css'

type Props = {
  company: Company
  salaryCycles: List<SalaryCycle>
  employees: List<Employee>

  addAlert: addAlertSignature
  doManual: (salaryCycleID: string, salaryPeriodID: string, type: string, employeeIDs: string[]) => void
  addPayRoll: (payRoll: NewPayRoll) => void
}

export default function PayRollsAddEmployeeList(props: Props): ReactElement | null {
  type EmployeeData = {
    key: string
    id: string
    companyID: string
    periodID: string
    cycleID: string
    imageUrl?: string
    name?: string
    email?: string
    changed: AdjustmentList[]
  }
  const [employeeList, setEmployeeList] = useState<EmployeeData[]>([])

  const [page, setPage] = useState(0)
  const [chosenEmployeeID, setChosenEmployeeID] = useState('')
  const [chosenEmployee, setChosenEmployee] = useState<Employee>()
  const [listEmployeeIDs, setLisetEmployeeIDs] = useState<Set<string>>(new Set<string>())
  const [period, setPeriod] = useState<string[]>(['2024-01-01', '2024-01-31'])
  const [preSelect, setPreSelect] = useState<string[]>([])

  const [chosenPeriodID, setChosenPeriodID] = useState<string>('')
  const [chosenPeriod, setChosenPeriod] = useState<SalaryPeriod>()

  const [chosenSalaryCycle, setChosenSalaryCycle] = useState<SalaryCycle>()
  const [chosenSalaryCycleID, setChosenSalaryCycleID] = useState<string>('')

  const [confirmSubmitModal, setConfirmSubmitModal] = useState<boolean>(false)
  const [loadingMessage, setLoadingMessage] = useState<string>('')
  const [modalState, setModalState] = useState<string>('start')

  const employee = props.employees

  function submitEmployees(values: PayRollsAddResult) {
    if (values.type === 'ZeroTaxReport') {
      props.doManual(values.salaryCycleID, values.salaryPeriodID, values.type, values.employeeIDs)
      return
    }
    const ids = new Set<string>()
    for (const e of values.employeeIDs) {
      ids.add(e)
    }

    const cycle = props.salaryCycles.find((e) => e.id === values.salaryCycleID)
    if (!cycle) {
      return
    }
    setChosenSalaryCycle(cycle)
    setChosenSalaryCycleID(values.salaryCycleID)
    const foundPeriod = cycle.salaryPeriods.find((e) => e.id === values.salaryPeriodID)
    if (!foundPeriod) {
      return
    }
    setPeriod([foundPeriod.start, foundPeriod.end])
    setChosenPeriod(foundPeriod)
    setChosenPeriodID(values.salaryPeriodID)

    const newList = employeeList.filter((e) => ids.has(e.id))
    for (const id of [...ids]) {
      if (!employeeList.find((e) => e.id === id)) {
        const e = employee.find((e) => e.id === id)
        if (!e) continue
        const entry: EmployeeData = {
          key: Math.random().toString(36).substring(2, 10),
          id: e.id,
          companyID: props.company.id,
          periodID: values.salaryPeriodID,
          cycleID: values.salaryCycleID,
          imageUrl: e.profileImageURL,
          name: e.name,
          email: e.email,
          changed: [],
        }
        newList.push(entry)
      }
    }

    setEmployeeList(newList)
    setLisetEmployeeIDs(ids)
    setPage(1)
  }

  function saveEmployeeData(id: string, change: AdjustmentList[]) {
    const idx = employeeList.findIndex((e) => e.id === id)
    employeeList[idx].changed = change.filter((e) => e.existing === false)
    setEmployeeList(employeeList)
    setPage(1)
  }

  function getAdjustments(id: string): AdjustmentList[] {
    const idx = employeeList.findIndex((e) => e.id === id)
    return employeeList[idx].changed
  }

  const openEmployee = (id: string) => {
    for (const e of props.employees) {
      if (e.id === id) {
        setChosenEmployee(e)
        setChosenEmployeeID(id)
        break
      }
    }
    setPage(2)
  }

  const cancelToZero = () => {
    setPreSelect([...listEmployeeIDs])
    setPage(0)
  }

  function confirmSubmit() {
    setConfirmSubmitModal(true)
  }

  function submitAdjustments() {
    const f = JSON.stringify(employeeList)
    setModalState('process')
    setLoadingMessage(t('pay_rolls.manual.saving.staged_file'))
    postStagedFile(f)
      .then((resp) => {
        postStagedImports('JSONFile', props.company.id, resp.data.id, chosenPeriodID)
          .then((resp) => {
            resp.data.stagedImportID
            setLoadingMessage(t('pay_rolls.manual.saving.pay_roll'))
            applyStagedImport(resp.data.stagedImportID)
              .then(() => {
                props.addAlert('success', t('pay_rolls.add.alert.success'), { timeout: 5 })
                jsBrowserHistory.push('/' + paths.PAY_ROLLS)
                setModalState('done')
              })
              .catch(() => {
                setLoadingMessage(t('pay_rolls.manual.saving.load_error'))
              })
          })
          .catch(() => {
            setLoadingMessage(t('pay_rolls.manual.saving.load_error'))
          })
      })
      .catch(() => {
        setLoadingMessage(t('pay_rolls.manual.saving.load_error'))
      })
  }

  function payRollSubmit() {
    setModalState('process')
    setLoadingMessage(t('pay_rolls.manual.saving.pay_roll'))
    props.addPayRoll({
      companyID: props.company.id,
      type: 'Extra',
      salaryPeriodID: chosenPeriodID,
      dispositionDate: chosenPeriod?.dispositionDate || '',
      employeeIDs: employeeList.map((e) => e.id),
      paySlipIDs: [],
    })

    setTimeout(submitAdjustments, 300)
  }

  const employeeCount = props.employees.size
  const hasEmployees = employeeCount > 0
  const allEmployeesHaveChanges = employeeList.length > 0 && employeeList.some((e) => e.changed.length > 0)

  return (
    <>
      <div style={{ margin: '2em 0' }}>
        {page === 0 && (
          <>
            {hasEmployees ? (
              <PayRollsAddForm
                company={props.company}
                salaryCycles={props.salaryCycles}
                employees={props.employees}
                onSubmit={submitEmployees}
                preSelected={preSelect}
              />
            ) : (
              <div className="no-employees-prompt">
                <p>{t('pay_rolls.add.no_employees.description')}</p>
                <Link to={'/' + paths.EMPLOYEES + '/' + paths.ADD}>
                  <Button type="primary" size="large">
                    {t('pay_rolls.add.no_employees.link_text')}
                  </Button>
                </Link>
              </div>
            )}
          </>
        )}
        {page === 1 && (
          <>
            <Row>
              <Col span={12}>
                <Subtitle style={{ margin: '.5em 0' }}>{t('pay_rolls.manual.table.employees')}</Subtitle>
                <p>{t('pay_rolls.manual.table.employees.description')}</p>
              </Col>
              <Col span={10}>
                <Subtitle style={{ float: 'right', marginTop: '4.8em' }}>
                  {t('pay_rolls.manual.period')}: {formatDate(period[0])} - {formatDate(period[1])}
                </Subtitle>
              </Col>
            </Row>
            <Subcard className="employee-search-search-results">
              <Col>
                {employeeList.map((e) => {
                  return (
                    <Row className="employee-row" key={e.key} style={{ marginLeft: '.1em' }}>
                      <Col span={10}>
                        <Headline>
                          <UserImage src={e.imageUrl} name={e.name || e.email} />
                          <div className="employee-name-spacing">
                            <span>{e.name || e.email}</span>
                          </div>
                        </Headline>
                      </Col>
                      <Col span={10} className="employee-item-space">
                        {e.changed.length == 0 ? (
                          <span>
                            <strong>{t('pay_rolls.manual.table.no_changes')}</strong>
                          </span>
                        ) : (
                          <span>{[...new Set(e.changed.map((e) => getTypeToText().get(e.type)))].join(', ')}</span>
                        )}
                      </Col>
                      <Col span={4}>
                        <div className="employee-item-button">
                          <Button onClick={() => openEmployee(e.id)}>{t('pay_rolls.manual.button.edit')}</Button>
                        </div>
                      </Col>
                    </Row>
                  )
                })}
                <div className="button-box">
                  <Button type="secondary" size="extra-extra-large" onClick={cancelToZero}>
                    {t('pay_rolls.manual.button.back')}
                  </Button>
                  <Button
                    type="primary"
                    size="extra-extra-large"
                    onClick={confirmSubmit}
                    disabled={!allEmployeesHaveChanges}
                  >
                    {t('pay_rolls.manual.button.save_adjustment')}
                  </Button>
                  <Button
                    type="secondary"
                    size="extra-extra-large"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                      props.doManual(
                        chosenSalaryCycleID,
                        chosenPeriodID,
                        'Extra',
                        employeeList.map((e) => e.id)
                      )
                    }}
                  >
                    {t('pay_rolls.add.form.submit_manual')}
                  </Button>
                </div>
              </Col>
            </Subcard>
          </>
        )}
        {page === 2 && (
          <PayRollAddSingleEmployee
            employeeID={chosenEmployeeID}
            employee={chosenEmployee}
            company={props.company}
            period={period}
            chosenSalaryCycle={chosenSalaryCycle}
            chosenSalaryCycleID={chosenSalaryCycleID}
            chosenPeriod={chosenPeriod}
            chosenPeriodID={chosenPeriodID}
            saveData={saveEmployeeData}
            getAdjustments={getAdjustments}
          />
        )}
      </div>
      <Modal
        visible={confirmSubmitModal}
        footer={null}
        onCancel={() => {
          setConfirmSubmitModal(false)
          setModalState('start')
        }}
      >
        <div className="pay-roll-confirm-modal">
          <Title>{t('pay_rolls.manual.popup_confirm_title')}</Title>
          <p>{t('pay_rolls.manual.popup_confirm_text')}</p>
          {loadingMessage !== '' && <p>{loadingMessage}</p>}
          <Row>
            <Col span={24}>
              <Button size="extra-extra-large" type="primary" disabled={modalState != 'start'} onClick={payRollSubmit}>
                {t('pay_rolls.manual.popup_confirm_submit')}
              </Button>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  )
}
